import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { updateUser } from '../store/authSlice';
import Layout from './common/Layout';
import UserManagement from './UserManagement';
import ContactManagement from './ContactManagement';
import ProfileManagement from './ProfileManagement';
import WhatsAppConnect from './WhatsAppConnect';
import BulkMessaging from './BulkMessaging';
import LidMappings from './LidMappings';
import Contacts from './Contacts';
import ContactGroups from './ContactGroups';
import BlockedUsers from './BlockedUsers';
import PackageManagement from './PackageManagement';
import OrderManagement from './OrderManagement';
import TotalEarnings from './TotalEarnings';
import SubscriptionPlans from './SubscriptionPlans';
import SubscriptionStatusWidget from './SubscriptionStatusWidget';
import { useSubscriptionCheck } from '../hooks/useSubscriptionCheck';

const GET_CURRENT_USER = gql`
  query GetCurrentUser($id: ID!) {
    user(id: $id) {
      id
      username
      firstName
      lastName
      email
      mobile
      ownerName
      companyName
      businessIndustry
      role
      is_active
      email_verified
      email_verified_at
      profile_image
      created_at
      updated_at
    }
  }
`;

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('dashboard');

  // Fetch fresh user data on mount to ensure we have latest info
  const { data: userData } = useQuery(GET_CURRENT_USER, {
    variables: { id: user?.id },
    skip: !user?.id,
    fetchPolicy: 'network-only'
  });

  // Update Redux when fresh user data is fetched
  useEffect(() => {
    if (userData?.user) {
      dispatch(updateUser(userData.user));
    }
  }, [userData, dispatch]);

  // Handle navigation from other components
  useEffect(() => {
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection === 'profile' ? 'super-admin-management' : location.state.activeSection);
    }
  }, [location.state]);

  // Subscription check hook
  const {
    showSubscriptionPopup,
    dismissPopup,
    onSubscriptionSuccess
  } = useSubscriptionCheck(user);

  // Security: Redirect restricted users away from unauthorized sections
  useEffect(() => {
    if (user?.role === 'user_admin') {
      // Allow dashboard, contact-management, staff-management, order-management, and super-admin-management
      if (activeSection !== 'dashboard' &&
        activeSection !== 'contact-management' &&
        activeSection !== 'staff-management' &&
        activeSection !== 'order-management' &&
        activeSection !== 'super-admin-management') {
        console.log(`🔒 Access denied: ${user.role} tried to access ${activeSection}, redirecting to dashboard`);
        setActiveSection('dashboard');
      }
    } else if (user?.role === 'user_staff') {
      // Only allow dashboard and super-admin-management
      if (activeSection !== 'dashboard' && activeSection !== 'super-admin-management') {
        console.log(`🔒 Access denied: ${user.role} tried to access ${activeSection}, redirecting to dashboard`);
        setActiveSection('dashboard');
      }
    }
  }, [activeSection, user?.role]);

  // Listen for profile switch event from UserManagement
  useEffect(() => {
    const handleSwitchToProfile = () => {
      setActiveSection('super-admin-management');
    };

    window.addEventListener('switchToProfile', handleSwitchToProfile);
    return () => {
      window.removeEventListener('switchToProfile', handleSwitchToProfile);
    };
  }, []);

  const handleProfileClick = () => {
    setActiveSection('super-admin-management');
  };

  const handleProfileImageClick = () => {
    // Only handle image upload when in ProfileManagement section
    if (activeSection === 'super-admin-management') {
      // Trigger the image upload in ProfileManagement
      window.dispatchEvent(new CustomEvent('profileImageClick'));
    } else {
      // Navigate to profile section first
      setActiveSection('super-admin-management');
    }
  };

  const renderContent = () => {
    // Security check: restrict access for user_admin and user_staff
    if (user?.role === 'user_admin') {
      // Allow dashboard, contact-management, staff-management, order-management, and super-admin-management (Profile)
      if (activeSection !== 'dashboard' &&
        activeSection !== 'contact-management' &&
        activeSection !== 'staff-management' &&
        activeSection !== 'order-management' &&
        activeSection !== 'super-admin-management') {
        // Redirect to dashboard if trying to access restricted section
        setActiveSection('dashboard');
        return (
          <div className="p-8 animate-in fade-in duration-500">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <span className="material-icons-round text-red-500 text-4xl mb-4 block">security</span>
              <h2 className="font-medium">Access Restricted</h2>
              <p className="text-red-600 text-sm">You don't have permission to access this section.</p>
            </div>
          </div>
        );
      }
    } else if (user?.role === 'user_staff') {
      // Only allow dashboard and super-admin-management (Profile)
      if (activeSection !== 'dashboard' && activeSection !== 'super-admin-management') {
        // Redirect to dashboard if trying to access restricted section
        setActiveSection('dashboard');
        return (
          <div className="p-8 animate-in fade-in duration-500">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <span className="material-icons-round text-red-500 text-4xl mb-4 block">security</span>
              <h2 className="font-medium">Access Restricted</h2>
              <p className="text-red-600 text-sm">You don't have permission to access this section.</p>
            </div>
          </div>
        );
      }
    }

    switch (activeSection) {
      case 'whatsapp':
        return <WhatsAppConnect />;
      case 'bulk-messaging':
        return <BulkMessaging />;
      case 'contacts':
        return <Contacts />;
      case 'contact-management':
        return <ContactManagement />;
      case 'contact-groups':
        return <ContactGroups />;
      case 'blocked-users':
        return <BlockedUsers />;
      case 'lid-mappings':
        return <LidMappings />;
      case 'user-management':
        return <UserManagement />;
      case 'staff-management':
        return <UserManagement />;
      case 'super-admin-management':
        return <ProfileManagement />;
      case 'package-management':
        return <PackageManagement />;
      case 'order-management':
        return <OrderManagement />;
      case 'total-earnings':
        return <TotalEarnings />;
      default:
        return (
          <div className="p-4 md:p-6 max-w-[1400px] mx-auto animate-in slide-in-from-bottom-6 duration-700">
            <div className="mb-6 md:mb-8 relative">
              <div className="absolute -left-4 md:-left-8 top-1/2 -translate-y-1/2 w-1 h-10 md:h-12 bg-bulky-blue-500 rounded-full shadow-[0_0_15px_rgba(40,92,237,0.5)]"></div>
              <h2 className="text-2xl font-medium text-bulky-navy-800 tracking-tight">
                Welcome, {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.name || user?.username}
              </h2>
              <p className="text-bulky-navy-400 text-[8px] md:text-[9px] font-normal tracking-wider md:tracking-wider ml-1">Access Level: <span className="text-bulky-blue-500">{user?.role?.replace('_', ' ')}</span></p>
            </div>

            {/* Subscription Status Widget - Only for user_admin */}
            {user?.role === 'user_admin' && (
              <div className="mb-6">
                <SubscriptionStatusWidget />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-8 md:mb-10">
              <div className="lg:col-span-8 bg-white rounded-[1.5rem] shadow-sm border border-gray-100 overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-bulky-navy-500/5 rounded-full -mr-48 -mt-48 transition-transform duration-1000 group-hover:scale-110"></div>
                <div className="bg-white p-6 flex items-center justify-between relative z-10 border-b border-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-bulky-blue-50 flex items-center justify-center text-bulky-blue-500">
                      <span className="material-icons-round text-xl">fingerprint</span>
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-bulky-navy-900">User Profile</h2>
                      <p className="text-bulky-navy-400 text-[10px] font-medium tracking-wide">Your account information</p>
                    </div>
                  </div>
                  <div className="px-4 py-1.5 bg-bulky-blue-50 text-bulky-blue-600 rounded-xl text-[10px] font-semibold tracking-wide">
                    {user?.role}
                  </div>
                </div>
                <div className="p-6 md:p-8 relative z-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-6 gap-x-10">
                    {[
                      {
                        label: 'Full Name', val: user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.name || user?.username
                      },
                      { label: 'Username', val: user?.username },
                      { label: 'Email Address', val: user?.email },
                      { label: 'Mobile Number', val: user?.mobile }
                    ].map((info, i) => (
                      <div key={i} className="space-y-1.5">
                        <p className="text-[9px] font-medium text-bulky-navy-400 uppercase tracking-wider leading-none ml-0.5">{info.label}</p>
                        <p className="text-sm md:text-base font-medium text-bulky-navy-800 tracking-tight break-all md:break-normal">{info.val}</p>
                        <div className="w-full h-px bg-gray-50"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 bg-white rounded-[1.5rem] p-6 text-bulky-navy-800 relative overflow-hidden group shadow-sm border border-gray-100">
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-bulky-blue-50 rounded-full blur-[80px] transition-all duration-1000 group-hover:scale-150 group-hover:-translate-x-10 opacity-70"></div>
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-6">
                    <h2 className="text-base font-semibold text-bulky-navy-900">System Status</h2>
                    <p className="text-bulky-navy-400 text-[10px] font-medium tracking-wide">Current system information</p>
                  </div>

                  <div className="space-y-3 flex-1">
                    {[
                      { label: 'User Role', val: user?.role, color: 'text-bulky-blue-500' },
                      { label: 'Status', val: 'Active', color: 'text-emerald-500' },
                      { label: 'Uptime', val: '99.98%', color: 'text-amber-500' }
                    ].map((stat, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-100 transition-all hover:bg-gray-50">
                        <span className="text-bulky-navy-500 text-[10px] font-semibold">{stat.label}</span>
                        <span className={`font-bold tracking-wide text-[11px] ${stat.color}`}>{stat.val}</span>
                      </div>
                    ))}
                  </div>

                  <button className="w-full mt-6 py-4 bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white rounded-2xl font-medium shadow-lg shadow-bulky-blue-500/10 transition-all active:scale-95 text-[11px] tracking-wide">
                    View Activity Logs
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h2 className="font-medium">
                <span className="w-6 md:w-10 h-[2px] bg-bulky-blue-500 rounded-full"></span>
                Available Features
              </h2>
              <div className="flex gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-bulky-blue-500"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-bulky-navy-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-bulky-navy-100"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {[
                { id: 'whatsapp', icon: 'phonelink_ring', title: 'WhatsApp', desc: 'Manage Numbers' },
                { id: 'bulk-messaging', icon: 'send', title: 'Bulk Messages', desc: 'Send Campaigns' },
                { id: 'contacts', icon: 'person', title: 'Contacts', desc: 'Contact List' },
                { id: 'contact-groups', icon: 'group', title: 'Groups', desc: 'Manage Groups' },
                { id: 'blocked-users', icon: 'block', title: 'Blocked Users', desc: 'Blocked List' },
                { id: 'lid-mappings', icon: 'link', title: 'ID Mappings', desc: 'Link IDs' },
                { id: 'user-management', icon: 'manage_accounts', title: 'Users', desc: 'Manage Users' },
                { id: 'package-management', icon: 'inventory_2', title: 'Plans', desc: 'Manage Plans' },
              ].map((item) => (
                <div
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className="bg-white group p-5 rounded-[1.5rem] border border-gray-100 cursor-pointer shadow-sm hover:shadow-[0_15px_40px_-10px_rgba(40,92,237,0.12)] hover:border-bulky-blue-100 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-transparent group-hover:bg-bulky-blue-500 transition-all"></div>
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-bulky-blue-50 transition-all duration-300">
                    <span className="material-icons-round text-xl text-bulky-navy-200 group-hover:text-bulky-blue-500 transition-colors duration-300">{item.icon}</span>
                  </div>
                  <h2 className="font-medium">
                    {item.title}
                  </h2>
                  <p className="text-gray-400 text-[7.5px] font-normal uppercase tracking-[0.2em]">{item.desc}</p>

                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0 duration-300">
                    <span className="material-icons-round text-bulky-blue-500 text-lg">arrow_forward</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <Layout
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      onProfileClick={handleProfileClick}
      onProfileImageClick={handleProfileImageClick}
    >
      {renderContent()}

      {/* Subscription Plans Popup */}
      <SubscriptionPlans
        isOpen={showSubscriptionPopup}
        onClose={dismissPopup}
        onSuccess={onSubscriptionSuccess}
      />
    </Layout>
  );
};


export default Dashboard;
